import { useEffect } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { Autocomplete, Checkbox, EllipsisMenu, Input } from '@weareopenr/component-library';
import { IOptionGroupItem, IOptionGroupValues } from '../types';
import styles from '../CreateOptionGroupModal.module.scss';
import { AutocompleteOptionWithRanges } from 'src/components/AutocompleteOptionWithRanges';
import { EntitySource } from '../../../constants';
import { Range } from '@weareopenr/types';
import type { IOption } from '@weareopenr/component-library';
import { useInheritPosId } from '../../../hooks/useInheritPosId';

export interface IModifiersProps {
  index: number;
  modifiers: IOptionGroupItem[];
  ellipsisMenuOptions: IOption[];
  useFormReturn: UseFormReturn<IOptionGroupValues>;
  onLoadMore: () => void;
  setSearchTerm: (term: string) => void;
  groupContentNameSingular: string;
}

interface ISelectedModifierProps {
  name: string;
  value: string;
  ranges: Range[];
}

const OptionGroupOption = ({
  index,
  modifiers,
  ellipsisMenuOptions,
  useFormReturn,
  onLoadMore,
  setSearchTerm,
  groupContentNameSingular,
}: IModifiersProps) => {
  const {
    control,
    watch,
    setValue,
    getValues,
    register,
    trigger,
    clearErrors,
    formState: { errors },
  } = useFormReturn;
  const watchModifier = watch(`modifiers.${index}`);

  const modifierOptions: { name: string; value: string; ranges: Range[] }[] = modifiers.map((modifier) => ({
    name: modifier.name,
    value: modifier.parentItemId || modifier.optionItemId,
    ranges: modifier.ranges,
  }));

  useEffect(() => {
    const selectedItem = modifiers.find((modifier) => modifier.optionItemId === watchModifier.parentItemId);
    if (selectedItem) {
      setValue(`modifiers.${index}.name`, selectedItem.name);
    }
  }, [watch(`modifiers.${index}.parentItemId`)]);

  const useDefaultPrice = watch(`modifiers.${index}.useDefaultPrice`);
  useEffect(() => {
    if (useDefaultPrice) {
      setValue(`modifiers.${index}.valueWithTax`, null);
    }
  }, [useDefaultPrice]);

  const { inheritPosId, posIdRegisterOptions } = useInheritPosId({
    index,
    formReturn: { watch, setValue, getValues, register, trigger },
  });

  const validateModifier = (modifier: IOptionGroupItem, { ranges, source }: IOptionGroupValues) => {
    if (source === EntitySource.LIBRARY || !modifier.ranges || modifier.ranges.length === 0 || ranges.length === 0) {
      return true;
    }
    return !(
      ranges &&
      ranges?.length > 0 &&
      modifier.ranges &&
      !modifier.ranges.map((range) => range.range_id).some((range) => ranges.includes(range))
    );
  };

  const validation = {
    validate: {
      exists: (v) => v.optionItemId !== '' || 'Please select a modifier',
      nonMatchingModifierRange: validateModifier,
    },
  };

  const isUnset = (value: number | null | undefined) => value === null || value === undefined || isNaN(value);
  const preserveNumber = (value: number | null | undefined) => (isUnset(value) ? undefined : value);

  return (
    <>
      <div>
        <Controller
          name={`modifiers.${index}`}
          control={control}
          rules={validation}
          render={({ field: { name, value }, fieldState: { error } }) => {
            const modifierValue = value as IOptionGroupItem;
            const selectedId = modifierValue?.parentItemId || modifierValue?.optionItemId || undefined;
            // The selected modifier may not be in the current server-side search results,
            // so seed it from the form state to keep its label visible
            const selectableOptions =
              selectedId && !modifierOptions.some((option) => option.value === selectedId)
                ? [
                    { name: modifierValue.name, value: selectedId, ranges: modifierValue.ranges ?? [] },
                    ...modifierOptions,
                  ]
                : modifierOptions;
            return (
              <div className={styles['autocomplete']}>
                <Autocomplete
                  name={name}
                  label={groupContentNameSingular}
                  filter="none"
                  options={selectableOptions.map((option) => ({
                    value: option.value,
                    label: option.name,
                    content: <AutocompleteOptionWithRanges option={option} />,
                  }))}
                  value={selectedId}
                  onSearchChange={setSearchTerm}
                  onLoadMore={onLoadMore}
                  error={error?.message}
                  onChange={(newId) => {
                    const newValue: ISelectedModifierProps | null =
                      selectableOptions.find((option) => option.value === newId) ?? null;
                    // Selecting a different item doesn't change min/max/default, so their
                    // error state shouldn't change either - trigger() below revalidates the
                    // whole modifier object (needed for the exists/nonMatchingModifierRange
                    // rules) which would otherwise surface unrelated, pre-existing errors on
                    // these untouched fields.
                    const hadMinError = Boolean(errors?.modifiers?.[index]?.min);
                    const hadMaxError = Boolean(errors?.modifiers?.[index]?.max);
                    const hadDefaultError = Boolean(errors?.modifiers?.[index]?.default);

                    if (newValue === null) {
                      setValue(`modifiers.${index}`, {
                        optionItemId: '',
                        parentItemId: '',
                        name: '',
                        posId: '',
                        valueWithTax: null,
                        useDefaultPrice: false,
                        inheritPosId: true,
                        min: preserveNumber(watchModifier.min),
                        max: preserveNumber(watchModifier.max),
                        default: preserveNumber(watchModifier.default),
                      });
                      // setValue on the whole nested object doesn't reliably sync a checkbox's
                      // DOM ref, so the inheritPosId checkbox above can silently stay unchecked -
                      // set it directly here too to force the DOM (and posId's disabled/required
                      // state) back in sync.
                      setValue(`modifiers.${index}.inheritPosId`, true);
                    } else {
                      setValue(`modifiers.${index}`, {
                        ...newValue,
                        parentItemId: newValue.value,
                        optionItemId: newValue.value,
                        valueWithTax: null,
                        posId: null,
                        useDefaultPrice: false,
                        inheritPosId: watchModifier.inheritPosId,
                        min: preserveNumber(watchModifier.min),
                        max: preserveNumber(watchModifier.max),
                        default: preserveNumber(watchModifier.default),
                      });
                    }
                    trigger(`modifiers.${index}`).then(() => {
                      if (!hadMinError) clearErrors(`modifiers.${index}.min`);
                      if (!hadMaxError) clearErrors(`modifiers.${index}.max`);
                      if (!hadDefaultError) clearErrors(`modifiers.${index}.default`);
                    });
                  }}
                />
              </div>
            );
          }}
        />
      </div>
      <div className={styles['pos-id-checkbox']}>
        <Checkbox key={index} name={`modifiers.${index}.inheritPosId`} register={register} />
      </div>

      <Input
        type="text"
        label="POS ID"
        register={posIdRegisterOptions}
        disabled={inheritPosId}
        error={useFormReturn.formState.errors?.modifiers?.[index]?.posId?.message}
      />

      <div className={styles['quantities-container']}>
        <Input
          type="number"
          label="Default"
          register={register(`modifiers.${index}.default`, {
            valueAsNumber: true,
            onChange: () => {
              trigger(`modifiers.${index}.min`);
              trigger(`modifiers.${index}.max`);
            },
            validate: (value, formValues) => {
              if (isUnset(value)) return true;
              if (value < 0) return 'Default must be a positive number';
              const minValue = formValues.modifiers[index].min;
              const maxValue = formValues.modifiers[index].max;
              if ((!isUnset(minValue) && value < minValue) || (!isUnset(maxValue) && value > maxValue))
                return 'Default must sit between option min and max';
              return true;
            },
          })}
          error={useFormReturn.formState.errors?.modifiers?.[index]?.default?.message}
        />
        <Input
          type="number"
          label="Min"
          register={register(`modifiers.${index}.min`, {
            valueAsNumber: true,
            onChange: () => {
              trigger(['min_choices', 'max_choices']);
              trigger(`modifiers.${index}.max`);
              trigger(`modifiers.${index}.default`);
            },
            validate: (value, formValues) => {
              const defaultValue = formValues.modifiers[index].default;
              if (isUnset(value)) return isUnset(defaultValue) || 'Min is required when default is set';
              if (value < 0) return 'Min must be a positive number';
              const maxValue = formValues.modifiers[index].max;
              if (!isUnset(maxValue) && value > maxValue) return 'Min must be less than max';
              return true;
            },
          })}
          error={useFormReturn.formState.errors?.modifiers?.[index]?.min?.message}
        />
        <Input
          type="number"
          label="Max"
          register={register(`modifiers.${index}.max`, {
            valueAsNumber: true,
            onChange: () => {
              trigger(['min_choices', 'max_choices']);
              trigger(`modifiers.${index}.default`);
              trigger(`modifiers.${index}.min`);
            },
            validate: (value, formValues) => {
              const defaultValue = formValues.modifiers[index].default;
              if (isUnset(value)) return isUnset(defaultValue) || 'Max is required when default is set';
              if (value < 0) return 'Max must be a positive number';
              const minValue = formValues.modifiers[index].min;
              if (isUnset(minValue)) return true;
              return value >= minValue || 'Max must be greater than or equal to min';
            },
          })}
          error={useFormReturn.formState.errors?.modifiers?.[index]?.max?.message}
        />
      </div>
      <div className={styles['ellipsis-menu-container']}>
        <EllipsisMenu options={ellipsisMenuOptions} variant="ghost" />
      </div>
    </>
  );
};

export default OptionGroupOption;
